import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApplicationForm, ApplicationFormData } from './ApplicationForm';
import CircularProgress from '@mui/material/CircularProgress';

type ApiApplicationRead = {
    id: number;
    organization: { id: number; name: string; full_name: string, short_name: string, legal_address: string, phone: string, email: string, created_at: string, updated_at: string };
    responsible_specialist: { id: number, organization_id: number; full_name: string; phone: string; email: string; created_at: string, updated_at: string };
    exam_export_folder: string;
    students: {
        id: number;
        full_name: string;
        email: string;
        student_programs: {
            program: { id: number; name: string; duration_hours: number; is_active: boolean, external_url: string, created_at: string, updated_at: string };
            start_date: string;
            end_date: string;
            exam_date: string;
        }[];
    }[];
};

export type Program = {
  id: string;
  name: string;
  durationHours: number;
  isActive: boolean;
  externalUrl?: string;
  createdAt: string;
  updatedAt: string;
};

const parseDate = (value?: string | null): Date | null => {
    if (!value) return null;
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
};

// Нормализация строкового представления даты (YYYY-MM-DD) для корректного сравнения сигнатур
const normalizeDateString = (value?: string | null): string | null => {
    if (!value) return null;
    const parsed = new Date(value);
    if (isNaN(parsed.getTime())) return null;
    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const day = String(parsed.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const mapApiToFormData = (apiData: ApiApplicationRead): ApplicationFormData => {
    return {
        subfolderName: apiData.exam_export_folder || '',
        organization: {
            id: String(apiData.organization.id),
            name: apiData.organization.short_name || apiData.organization.full_name,
            fullName: apiData.organization.full_name,
            shortName: apiData.organization.short_name || undefined,
            address: apiData.organization.legal_address || undefined,
            email: apiData.organization.email || undefined,
            phone: apiData.organization.phone || undefined,
            createdAt: apiData.organization.created_at,
        },
        specialist: {
            id: String(apiData.responsible_specialist.id),
            organizationId: String(apiData.responsible_specialist.organization_id),
            fullName: apiData.responsible_specialist.full_name,
            phone: apiData.responsible_specialist.phone || undefined,
            email: apiData.responsible_specialist.email || undefined,
            createdAt: apiData.responsible_specialist.created_at,
            updatedAt: apiData.responsible_specialist.updated_at,
        },
        students: apiData.students.map(student => ({
            id: String(student.id),
            full_name: student.full_name,
            email: student.email,
            programs: student.student_programs.map(sp => ({
                program: {
                    id: String(sp.program.id),
                    name: sp.program.name,
                    durationHours: sp.program.duration_hours,
                    isActive: sp.program.is_active,
                    externalUrl: sp.program.external_url || undefined,
                    createdAt: sp.program.created_at,
                    updatedAt: sp.program.updated_at,
                },
                theoretical_start_date: sp.start_date,
                theoretical_end_date: sp.end_date,
                exam_date: sp.exam_date,
            }))
        }))
    };
};

interface EditApplicationPageProps {
    onLogout: () => void;
}

const EditApplicationPage: React.FC<EditApplicationPageProps> = ({ onLogout }) => {
    const { applicationId } = useParams<{ applicationId: string }>();
    const navigate = useNavigate();
    const [initialData, setInitialData] = useState<ApplicationFormData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [commonProgramIds, setCommonProgramIds] = useState<Set<string>>();
    const [commonProgramDetails, setCommonProgramDetails] = useState<Record<string, any>>();
    const [commonPrograms, setCommonPrograms] = useState<Program[]>();

    const token = localStorage.getItem('token');
    const storedUserStr = localStorage.getItem('user');
    const user = storedUserStr ? JSON.parse(storedUserStr) : null;

    const username = user?.full_name || user?.email || '—';

    let roleName = 'user';
    if (user?.role) {
        if (typeof user.role === 'string') {
            roleName = user.role;
        } else if (typeof user.role === 'object' && user.role.name) {
            roleName = user.role.name;
        }
    }

    useEffect(() => {
        if (!token) {
            navigate('/login');
        }
    }, [token, navigate]);

    useEffect(() => {
        const fetchApplication = async () => {
            if (!token) return;

            setLoading(true);
            setError(null);
            try {
                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/applications/${applicationId}`, {
                    headers: {
                        "Accept": "application/json",
                        "Authorization": token
                    }
                });

                if (res.status === 401) {
                    onLogout();
                    return;
                }

                if (!res.ok) {
                    throw new Error(`Не удалось загрузить данные заявки: ${res.status}`);
                }
                const apiData: ApiApplicationRead = await res.json();

                const formData = mapApiToFormData(apiData);
                setInitialData(formData);

                if (formData.students && formData.students.length > 0) {
                    // 1. Группируем все записи о прохождении программ по ID программы
                    const programInstances = new Map<string, {
                        program: Program;
                        dates: Array<{ start: string | null; end: string | null; exam: string | null }>;
                    }>();

                    formData.students.forEach((student) => {
                        student.programs.forEach((p) => {
                            const progId = p.program.id;
                            if (!programInstances.has(progId)) {
                                programInstances.set(progId, {
                                    program: p.program,
                                    dates: []
                                });
                            }
                            programInstances.get(progId)!.dates.push({
                                start: normalizeDateString(p.theoretical_start_date),
                                end: normalizeDateString(p.theoretical_end_date),
                                exam: normalizeDateString(p.exam_date)
                            });
                        });
                    });

                    const restoredIds = new Set<string>();
                    const restoredDetails: Record<string, any> = {};
                    const restoredPrograms: Program[] = [];

                    // 2. Проверяем каждую программу: совпадают ли даты у всех студентов, кто её изучает
                    programInstances.forEach((inst, progId) => {
                        const firstDate = inst.dates[0];
                        const allDatesMatch = inst.dates.every(d =>
                            d.start === firstDate.start &&
                            d.end === firstDate.end &&
                            d.exam === firstDate.exam
                        );

                        if (allDatesMatch) {
                            restoredIds.add(progId);
                            restoredPrograms.push(inst.program);
                            restoredDetails[progId] = {
                                theoretical_start_date: parseDate(firstDate.start),
                                theoretical_end_date: parseDate(firstDate.end),
                                exam_date: parseDate(firstDate.exam)
                            };
                        }
                    });

                    if (restoredIds.size > 0) {
                        setCommonProgramIds(restoredIds);
                        setCommonProgramDetails(restoredDetails);
                        setCommonPrograms(restoredPrograms);
                    }
                }

            } catch (err: any) {
                console.error(err);
                setError(err.message || 'Произошла ошибка при загрузке.');
            } finally {
                setLoading(false);
            }
        };

        if (applicationId) {
            fetchApplication();
        }
    }, [applicationId, token, onLogout]);

    const handleUpdateApplication = async (formData: ApplicationFormData) => {
        if (!token) {
            onLogout();
            throw new Error("Сессия истекла");
        }

        const payload = {
            organization_id: Number(formData.organization!.id),
            responsible_specialist_id: Number(formData.specialist!.id),
            exam_export_folder: formData.subfolderName.trim(),
            students: formData.students.map(student => ({
                student_id: student.id.includes('-') ? undefined : Number(student.id),
                full_name: student.full_name,
                email: student.email,
                programs: student.programs.map(pa => ({
                    program_id: Number(pa.program.id),
                    theoretical_start_date: pa.theoretical_start_date || null,
                    theoretical_end_date: pa.theoretical_end_date || null,
                    exam_date: pa.exam_date || null,
                })),
            })),
        };

        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/applications/${applicationId}/full`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": token
            },
            body: JSON.stringify(payload),
        });

        if (res.status === 401) {
            onLogout();
            throw new Error("Сессия истекла");
        }

        if (!res.ok) {
            let errorMessage = `Ошибка сервера (${res.status})`;
            try {
                const errorBody = await res.json();
                errorMessage = errorBody?.detail || JSON.stringify(errorBody) || errorMessage;
            } catch (e) {
                // Ignore
            }
            throw new Error(errorMessage);
        }

        const data = await res.json();
        navigate('/applications');
        return data;
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex justify-center items-center">
                <CircularProgress />
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 p-6">
                <div className="max-w-7xl mx-auto px-4 py-4 rounded-md bg-red-50 border border-red-200">
                    <p className="text-red-600 font-medium">{error}</p>
                </div>
            </div>
        );
    }

    if (!initialData) {
        return null;
    }

    return (
        <ApplicationForm
            onLogout={onLogout}
            isEditing={true}
            initialData={initialData}
            onSubmit={handleUpdateApplication}
            username={username}
            role={roleName}
            initialCommonProgramIds={commonProgramIds}
            initialCommonProgramDetails={commonProgramDetails}
            initialCommonPrograms={commonPrograms}
        />
    );
};

export default EditApplicationPage;